import { estRefusAutoplay, reprendreAuPremierGeste } from '../utils/audioGesture';

﻿import { useEffect, useRef, useState } from 'react';
import { useIsFocused } from '@react-navigation/native';

const STORY_MP3_MAP: Record<string, string> = {
  egocrisis: 'EgoCrisis',
  foundation_gamble: 'FoundationGamble',
  data_storm: 'DataStorm',
  cloud_heist: 'CloudHeist',
  neural_override: 'NeuralOverride',
  pipeline_inferno: 'PipelineInferno',
  merge_conflict: 'MergeConflict',
  deadline_paradox: 'DeadlineParadox',
  quantum_gambit: 'QuantumGambit',
  arabic_modern: 'ArabicModern',
  learning_machine: 'LearningMachine',
  ink_protocol: 'InkProtocol',
};

const BASE_URL = 'https://ccpubmedia.blob.core.windows.net/music';

// LECTEUR UNIQUE pour toute l'app (CCT-136). En arrivant par une notification, deux instances
// de l'écran de mission peuvent coexister un instant (pile racine + pile interne du layout
// desktop) : chacune créait son propre Audio() et la piste se jouait EN DOUBLE, légèrement
// décalée. Un singleton au niveau module rend ce cas impossible, quel que soit le nombre de
// montages : on réutilise le lecteur si l'URL est la même, on le remplace sinon.
type Player = { url: string; audio: HTMLAudioElement; refs: number };
let player: Player | null = null;

function acquire(url: string): HTMLAudioElement {
  if (player && player.url === url) { player.refs++; return player.audio; }
  if (player) { player.audio.pause(); player.audio.src = ''; }   // changement de piste
  const audio = new Audio(url);
  audio.muted = muted;              // une piste qui démarre respecte la sourdine en cours
  player = { url, audio, refs: 1 };
  // Compteur observable par le harnais : un lecteur unique est la garantie qu'aucune piste
  // ne se superpose (l'objet Audio() vit hors du DOM, invisible autrement).
  if (typeof window !== 'undefined') (window as any).__ccMusicPlayers = 1;
  notify();
  return audio;
}

// État de SOURDINE global : la commande vit dans le header de l'app, pas dans l'écran de
// mission — il faut donc pouvoir la piloter sans passer par le hook de l'écran (CCT-136).
//
// ⚠️ ELLE NE COUPAIT QUE LA MUSIQUE. Les VOIX des répliques sont des objets `Audio()` créés
// par `useLineAudio`, sans aucun lien avec le lecteur musical : couper le son en pleine
// scène laissait les personnages parler. Or personne n'appuie sur « couper le son » en voulant
// dire « couper la moitié du son » — dans une scène, la voices EST le son.
//
// La sourdine est donc un état de l'APPLICATION, pas du lecteur de musique. Chaque élément
// audio s'y abonne : celui de la musique, et ceux des répliques, y compris ceux créés PENDANT
// que la sourdine est active (une réplique qui démarre en sourdine doit rester muette).
let muted = false;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((f) => f());

/** Éléments audio de répliques en cours, qui doivent suivre la sourdine. */
const voices = new Set<HTMLAudioElement>();

/**
 * Place un élément audio sous la sourdine globale. Rend la fonction qui l'en retire.
 *
 * ⚠️ LE DÉSABONNEMENT EST OBLIGATOIRE. Sans lui, chaque réplique jouée laisserait son élément
 * dans l'ensemble pour toute la session : une fuite qui grossit à chaque beat, et un
 * basculement de sourdine qui écrirait sur des éléments abandonnés.
 */
export function followMute(audio: HTMLAudioElement): () => void {
  audio.muted = muted;                 // une voices qui démarre respecte la sourdine en cours
  voices.add(audio);
  return () => { voices.delete(audio); };
}

/** Vrai si une piste OU une voices est en cours — le header n'affiche la commande que dans ce cas. */
export function isMusicActive(): boolean { return !!player || voices.size > 0; }
export function isMusicMuted(): boolean { return muted; }
export function toggleMusicMuted(): void {
  muted = !muted;
  if (player) player.audio.muted = muted;
  for (const a of voices) {
    try { a.muted = muted; } catch { /* élément déjà libéré */ }
  }
  notify();
}

// ATTÉNUATION PENDANT UNE RÉPLIQUE (CCT-226) — « la musique passe à très bas volume ».
//
// Le lecteur sait déjà régler son volume (`setVolume`) : couper n'était pas nécessaire, et
// couper puis relancer produit un à-coup audible à chaque réplique.
//
// L'état vit AU NIVEAU DU MODULE, comme la sourdine, et pour la même raison : c'est la scène
// qui parle, pas l'écran qui possède le hook. Passer par le hook obligerait à faire descendre
// un `setVolume` à travers tout l'arbre pour un réglage qui, lui, est global.
//
// ⚠️ COMPTEUR et non booléen : deux répliques peuvent se chevaucher (enchaînement automatique,
// rejeu manuel). Un booléen ferait remonter le volume à la fin de la PREMIÈRE, en pleine
// seconde. On ne remonte qu'au retour à zéro.
const VOLUME_ATTENUE = 0.06;   // audible, mais très en dessous d'une voices
const VOLUME_NOMINAL_DEFAUT = 0.3;   // musique d'ambiance : présente, jamais au premier plan
let attenuations = 0;
// Volume choisi par l'auditeur, s'il en a choisi un. Il vit ICI et non sur l'élément audio :
// l'élément est remplacé à chaque changement de piste, et le réglage serait perdu.
let volumeNominal = VOLUME_NOMINAL_DEFAUT;

/**
 * ⚠️ UN SEUL PROPRIÉTAIRE POUR LE VOLUME, ET C'EST CE MODULE.
 *
 * La version précédente en avait deux. `duckMusic` mémorisait « le volume d'avant »
 * pour le restituer, pendant que l'effet du hook écrivait `audio.volume = 0.3` à chacune de
 * ses exécutions — sans rien savoir d'une atténuation en cours. Deux écrivains, une seule
 * propriété, et une valeur mémorisée qui cessait de correspondre à la réalité dès qu'ils se
 * croisaient. La musique disparaissait sans que rien ne soit « en panne » : chaque moitié
 * faisait exactement ce pour quoi elle était écrite.
 *
 * On ne mémorise donc plus rien. Le volume se DÉDUIT de l'état — atténué ou non — et un seul
 * chemin l'écrit. Un état qu'on recalcule ne peut pas se désynchroniser.
 */
function volumeVoulu(): number {
  return attenuations > 0 ? VOLUME_ATTENUE : volumeNominal;
}

/** Applique le volume déduit. Le SEUL endroit qui écrit `audio.volume`. */
function applyVolume(): void {
  if (player) player.audio.volume = volumeVoulu();
}

/**
 * Volume d'écoute choisi par l'auditeur.
 *
 * ⚠️ IL NE S'ÉCRIT PAS DIRECTEMENT SUR L'ÉLÉMENT. C'était le troisième écrivain de la même
 * propriété : régler le volume pendant une réplique annulait l'atténuation, et la relever
 * ensuite écrasait le réglage. On enregistre l'intention, et un seul chemin l'applique.
 */
export function reglerVolumeMusique(v: number): void {
  volumeNominal = Math.max(0, Math.min(1, v));
  applyVolume();
}

/** Volume d'écoute courant, hors atténuation. */
export function volumeNominalMusique(): number {
  return volumeNominal;
}

/** Baisse la musique le temps d'une réplique. Rend la fonction qui la relève. */
export function duckMusic(): () => void {
  let rendu = false;
  attenuations++;
  applyVolume();
  return () => {
    if (rendu) return;                 // idempotent : un double appel ne doit pas décompter deux fois
    rendu = true;
    attenuations = Math.max(0, attenuations - 1);
    applyVolume();                 // ⚠️ ne remonte qu'au retour à ZÉRO (cf. volumeVoulu)
  };
}

/**
 * Remise à zéro des atténuations.
 *
 * Une réplique dont le nettoyage n'a pas tourné — onglet fermé pendant la lecture, erreur
 * remontée hors du composant — laisserait le compteur au-dessus de zéro POUR TOUTE LA
 * SESSION, et la musique définitivement basse. L'écran qui possède la musique repart donc
 * d'un état propre : aucune réplique ne peut être en cours puisqu'aucune n'est montée.
 */
export function resetDucking(): void {
  attenuations = 0;
  applyVolume();
}

/** Volume courant de la musique (diagnostic et tests). */
export function volumeMusique(): number | null {
  return player ? player.audio.volume : null;
}

/** Nombre de répliques en cours d'atténuation (diagnostic et tests). */
export function attenuationsEnCours(): number {
  return attenuations;
}

/**
 * État observable depuis la console : `__ccMusic()`.
 *
 * L'objet Audio() vit hors du DOM. Sans cette trace, « la musique a disparu » ne se
 * distingue pas d'« elle joue à 6 % », ni d'« elle est en sourdine » — trois causes
 * différentes, un seul symptôme, et aucun moyen de trancher depuis une capture d'écran.
 */
if (typeof window !== 'undefined') {
  (window as any).__ccMusic = () => ({
    piste: player?.url ?? null,
    volume: player ? player.audio.volume : null,
    attenuations,
    muted: muted || (player ? player.audio.muted : false),
    voices: voices.size,
    enPause: player ? player.audio.paused : null,
    position: player ? Math.round(player.audio.currentTime * 10) / 10 : null,
  });
}

/** Abonnement du header aux changements (piste démarrée/arrêtée, sourdine). */
export function subscribeMusic(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

function release(url: string): void {
  if (!player || player.url !== url) return;
  player.refs--;
  if (player.refs > 0) return;          // un autre écran écoute encore : on ne coupe pas
  player.audio.pause();
  player.audio.src = '';
  player = null;
  if (typeof window !== 'undefined') (window as any).__ccMusicPlayers = 0;
  notify();
}

/**
 * Musique d'ambiance d'une mission.
 *
 * `trackUrl` (CCT-130) : piste AUTHORÉE, déjà résolue par l'appelant selon la règle
 * élément > chapitre > mission. Elle prime sur la piste bundlée indexée par `storyKey` —
 * cette dernière retombe sur « egocrisis » pour toute mission créée dans le BO, donc sur la
 * musique d'une AUTRE histoire.
 *
 * La lecture s'arrête au démontage : sortir de la navigation d'une mission coupe le son
 * (c'est le `return` du `useEffect`, pas un appel explicite à faire depuis chaque écran).
 */
export function useMissionMusic(storyKey: string, trackUrl?: string | null) {
  // ⚠️ L'écran de mission RESTE MONTÉ dans la pile de navigation quand on revient sur
  // « Missions » : le nettoyage au démontage ne suffisait donc pas, la musique continuait.
  // On se cale sur le FOCUS : hors écran, plus de piste (CCT-146).
  const focused = useIsFocused();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolumeState] = useState(0.3);
  // currentTime and duration kept as refs to avoid re-renders on every tick
  const currentTimeRef = useRef(0);
  const durationRef = useRef(0);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof Audio === 'undefined') return;

    // Piste authorée : URL absolue servie par le runtime, ou simple nom de fichier du
    // conteneur `music` (tolérance pour une valeur saisie à la main dans le BO).
    if (!focused) return;                 // écran quitté : aucune piste ne doit tourner
    const authored = (trackUrl || '').trim();
    const fileName = STORY_MP3_MAP[storyKey];
    if (!authored && !fileName) return;

    const url = authored
      ? (authored.startsWith('http') ? authored : `${BASE_URL}/${authored}`)
      : `${BASE_URL}/${fileName}.mp3`;
    const audio = acquire(url);
    // Trace de la piste RÉELLEMENT jouée : l'objet Audio() n'est pas dans le DOM, donc
    // invisible pour un harnais. Sans elle, « ce n'est pas la bonne musique » n'est pas
    // vérifiable automatiquement (CCT-135).
    if (typeof window !== 'undefined') (window as any).__ccMusicSrc = url;
    // ⚠️ PAS D'ÉCRITURE DIRECTE ICI. Cet effet se rejoue à chaque changement de piste, de
    // focus, et à chaque remontage : écrire `0.3` en dur écrasait une atténuation en cours
    // et désynchronisait l'état qui sert à la relever.
    applyVolume();
    audio.loop = true;
    audioRef.current = audio;

    const handleTimeUpdate = () => { currentTimeRef.current = audio.currentTime; };
    const handleLoadedMetadata = () => { durationRef.current = audio.duration; };
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleError = () => {
      console.warn('[useMissionMusic] Audio failed to load:', url);
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('error', handleError);

    // ⚠️ UN REFUS D'AUTOPLAY N'EST PAS UNE PANNE. Sans geste préalable — F5, lien direct,
    // retour d'onglet — `play()` rejette avec `NotAllowedError`, et ce rejet était avalé dans
    // un `console.warn`. Mesuré en navigateur : 2 appels, 2 rejets, zéro erreur visible ; la
    // musique ne partait jamais et rien ne le disait. On réessaie au premier geste.
    let annulerReprise: (() => void) | null = null;
    audio.play().catch((err: Error) => {
      if (estRefusAutoplay(err)) {
        annulerReprise = reprendreAuPremierGeste(() => {
          audio.play().catch(() => { /* second refus : on n'insiste pas */ });
        });
        return;
      }
      console.warn('[useMissionMusic] Could not play audio:', err.message);
    });

    return () => {
      // Sans cette annulation, un écran démonté laisserait un écouteur qui relancerait la
      // musique d'une mission qu'on a quittée.
      annulerReprise?.();
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('error', handleError);
      release(url);
      audioRef.current = null;
      setIsPlaying(false);
      currentTimeRef.current = 0;
      durationRef.current = 0;
    };
  }, [storyKey, trackUrl, focused]);

  const toggleMute = () => {
    setIsMuted(prev => {
      const next = !prev;
      if (audioRef.current) {
        audioRef.current.muted = next;
      }
      return next;
    });
  };

  const play = () => {
    audioRef.current?.play().catch((err: Error) => {
      // Commande explicite : le geste vient d'avoir lieu, un refus ici est un vrai échec.
      console.warn('[useMissionMusic] play error:', err.message);
    });
  };

  const pause = () => {
    audioRef.current?.pause();
  };

  const seek = (seconds: number) => {
    if (audioRef.current) {
      audioRef.current.currentTime = seconds;
      currentTimeRef.current = seconds;
    }
  };

  const setVolume = (v: number) => {
    // Délégation : le module est le seul à écrire `audio.volume` (cf. `appliquerVolume`).
    const clamped = Math.max(0, Math.min(1, v));
    setVolumeState(clamped);
    reglerVolumeMusique(clamped);
  };

  return {
    isMuted,
    toggleMute,
    isPlaying,
    volume,
    play,
    pause,
    seek,
    setVolume,
  };
}
